import React, { useCallback, useEffect, useLayoutEffect, useState } from 'react';
import type { BasePaginationProps, FilterSearchPropsMenu } from '@components';
import { BasePagination, BaseSpin, BaseTabs, FilterSearch } from '@components';
import type { SelectedLeafInfo } from '@components/filter-checkbox-selector/helpers.ts';
import { createGenericHandlers, getSelectedLeaves } from '@components/filter-checkbox-selector/helpers.ts';
import type { AppDispatchFn } from '@hooks/reduxHooks.ts';
import { useAppDispatch, useAppSelector } from '@hooks/reduxHooks.ts';
import { useMounted } from '@hooks/useMounted.ts';
import { useLingui } from '@lingui/react/macro';
import { SharedPageWrapper } from '@routes/___shared___/styled.tsx';
import { PageHeader } from '@routes/profile-bar/___pages___/my-profile/styled.tsx';
import {
    ALL,
    ASSIGNED,
    NOTIFICATIONS_VIEW_SCROLLABLE_SELECTOR as SCROLLABLE_SELECTOR,
    PAGE_SIZE_OPTIONS,
    READ,
    UNREAD,
} from '@routes/sidebar/notifications/___shared___/constants.ts';
import {
    getNotificationFilters,
    NOTIFICATIONS_FILTER_MENU,
} from '@routes/sidebar/notifications/___shared___/filters.ts';
import { applyTabConstraint, isNotificationNew } from '@routes/sidebar/notifications/___shared___/helpers.ts';
import { useTabs } from '@routes/sidebar/notifications/___shared___/useTabs.tsx';
import type { UserNotificationRequest } from '@routes/sidebar/notifications/api/notifications.api.ts';
import {
    clearNotifications,
    getNotifications,
    markSeenNotifications,
    setScrollTopPosition,
    setSuppressViewLoading,
    updateNotificationRequest,
} from '@routes/sidebar/notifications/model/notifications.slice.ts';
import { TabsWrapper, TopSection, TotalNotifications } from '@routes/sidebar/notifications/styled.tsx';
import type { CommonSearchRequest } from '@types';
import { hidePagination, totalResults } from '@utils/pagination.ts';

const Notifications = () => {
    const { t } = useLingui();
    const isMounted = useMounted();
    const dispatch = useAppDispatch();

    const [current, setCurrent] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(10);
    const [activeTab, setActiveTab] = useState<string>(ALL);
    const [menu, setMenu] = useState<FilterSearchPropsMenu[]>(NOTIFICATIONS_FILTER_MENU);
    const [clearAll, setClearAll] = useState(false);

    const notifications = useAppSelector((state) => state.notifications.notificationsData);
    const notificationRequest = useAppSelector((state) => state.notifications.notificationRequest);
    const isGetNotificationsPending = useAppSelector((state) => state.notifications.isGetNotificationsPending);
    const suppressViewLoading = useAppSelector((state) => state.notifications.suppressViewLoading);
    const isReadNotificationSend = useAppSelector((state) => state.notifications.isReadNotificationSend);
    const scrollTopPosition = useAppSelector((state) => state.notifications.scrollTopPosition);
    const [inputSearchValue, setInputSearchValue] = useState(notificationRequest.textSearch);

    const selectedLeaves: SelectedLeafInfo[] = getSelectedLeaves(menu);
    const filtersCount = selectedLeaves.length + (notificationRequest.textSearch?.trim() !== '' ? 1 : 0);
    const notificationsTotalCount = notifications.pagination.totalCount;

    const tabs = useTabs(notifications.data ?? [], notifications.resultsDescription?.assignedNew ?? 0, activeTab);

    const updateRequestSilently = useCallback(
        (data: UserNotificationRequest) => (dispatch: AppDispatchFn) => {
            dispatch(setScrollTopPosition(null));
            dispatch(setSuppressViewLoading(true));
            dispatch(updateNotificationRequest(applyTabConstraint(activeTab, data)));
        },
        [activeTab],
    );

    const { onSearchTermChange, onCheckboxSelected, onClearAllFilters } = createGenericHandlers({
        dispatch,
        requestData: notificationRequest,
        updateMethod: updateRequestSilently,
        menu,
        setMenu,
        setClearAll,
        defaultMenu: NOTIFICATIONS_FILTER_MENU,
        supportsSort: false,
        supportsPagination: false,
        getUpdatedFilters: (selectedLeaves) => ({
            filters: getNotificationFilters(selectedLeaves),
        }),
    });

    useEffect(() => {
        void dispatch(getNotifications(notificationRequest));
    }, [dispatch, notificationRequest, isReadNotificationSend]);

    useEffect(() => {
        if (isMounted.current) {
            if (!notifications.data?.length) return; // wait until data arrives
            // we don't get new notifications after 'seen' as this will toggle 'new' state on list (it will flap)
            const seenIds: number[] = notifications.data.filter(isNotificationNew).map((n) => n.id);
            if (seenIds.length > 0) {
                void dispatch(markSeenNotifications(seenIds));
            }
        }
    }, [dispatch, isMounted, notifications.data]);

    useEffect(() => {
        setInputSearchValue(notificationRequest.textSearch);
    }, [notificationRequest.textSearch]);

    const onTabChange = (key: string) => {
        dispatch(setScrollTopPosition(null));
        onClearAllFilters();
        setCurrent(1);
        setActiveTab(key);
        setPageSize(10);

        const base: CommonSearchRequest = {
            pageNumber: 1,
            pageSize: 10,
            filters: null,
            textSearch: '',
        };

        if (key === UNREAD || key === READ) {
            dispatch(
                updateNotificationRequest({
                    ...base,
                    filters: { wasRead: [(key === READ).toString()] },
                }),
            );
        } else if (key === ASSIGNED) {
            dispatch(
                updateNotificationRequest({
                    ...base,
                    filters: { NotificationType: [ASSIGNED] },
                }),
            );
        } else if (key === ALL) {
            dispatch(
                updateNotificationRequest({
                    ...base,
                    filters: null,
                }),
            );
        } else {
            dispatch(updateNotificationRequest({ ...base }));
        }
    };

    const handlePageChange: BasePaginationProps['onChange'] = (page, size) => {
        setCurrent(page);
        setPageSize(size);

        dispatch(
            updateNotificationRequest({
                ...notificationRequest,
                pageNumber: page,
                pageSize: size,
            }),
        );
        dispatch(setScrollTopPosition(null));
    };

    const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        const valueLength = value.length;

        setInputSearchValue(value);
        dispatch(setScrollTopPosition(null));

        if (valueLength !== 1) {
            onSearchTermChange(value);
        }
    };

    const handleClearSearch = () => {
        setInputSearchValue('');
        dispatch(setScrollTopPosition(null));
        onSearchTermChange('');
    };

    const handleClearAll = () => {
        onTabChange(activeTab);
    };

    useEffect(() => {
        return () => {
            dispatch(clearNotifications());
        };
    }, [dispatch]);

    const setScrollPositionForToggleRead = useCallback(() => {
        if (typeof scrollTopPosition !== 'number') return;

        const el = document.querySelector<HTMLElement>(SCROLLABLE_SELECTOR);
        if (!el) return;

        const maxTop = Math.max(0, el.scrollHeight - el.clientHeight);
        el.scrollTop = Math.min(scrollTopPosition, maxTop);
    }, [scrollTopPosition]);

    useLayoutEffect(() => {
        setScrollPositionForToggleRead();
    }, [setScrollPositionForToggleRead, notifications.data]);

    if (isGetNotificationsPending && !suppressViewLoading) {
        return <BaseSpin size="large" />;
    }

    return (
        <SharedPageWrapper>
            <TopSection id={'notifications-header-top-section'}>
                <PageHeader>{t`Notifications`}</PageHeader>
                <div>
                    <FilterSearch
                        onCheckboxSelected={onCheckboxSelected}
                        onChangeSearch={handleSearch}
                        menu={menu}
                        clearAll={clearAll}
                        inputValue={inputSearchValue ?? ''}
                        onClearSearch={handleClearSearch}
                        searchHintText={t`Search by name`}
                        submenuRenderSide={'right'}
                        onClearAllFilters={handleClearAll}
                        filtersCount={filtersCount}
                        requestData={notificationRequest}
                        selectedLeaves={selectedLeaves}
                    />
                </div>
            </TopSection>
            <TabsWrapper>
                <BaseTabs activeKey={activeTab} items={tabs} onChange={onTabChange} />
                <TotalNotifications>
                    {totalResults(notifications)} {t`Notifications`}
                </TotalNotifications>
            </TabsWrapper>
            {!hidePagination(notificationsTotalCount) && (
                <BasePagination
                    align="end"
                    current={current}
                    pageSize={pageSize}
                    onChange={handlePageChange}
                    pageSizeOptions={PAGE_SIZE_OPTIONS}
                    total={notificationsTotalCount}
                />
            )}
        </SharedPageWrapper>
    );
};

export default Notifications;
